import strings from "global/constants/StringConstants";
import { fillCopy } from "helpers/methods";
import {
  FileField,
  RequestFile,
  RequestParticipant,
  SignRequest,
} from "models/interfaces";
import { ANY_SIGNER } from "screens/SignRequest/SignRequestHelper";
import {
  PresendFinding,
  PresendReviewDocument,
  PresendReviewField,
  PresendReviewRecipient,
  PresendReviewRequest,
  PresendReviewResponse,
} from "./PresendReviewTypes";

const copy = strings.presendReview;

// The AI's own caps: past them it answers 400. An envelope over them is not reviewed at all -- cutting it
// to fit would report the dropped fields' documents as unsigned and their recipients as unknown.
const MAX_REVIEW_FIELDS = 500;
const MAX_REVIEW_RECIPIENTS = 50;
const MAX_REVIEW_TEXT = 200;

/**
 * Editor field types (as `setCoordinates` normalises them) in the AI's vocabulary. A type missing here
 * is sent without a hint -- the AI rejects a type it does not know with a 400.
 */
const REVIEW_FIELD_TYPES: Record<string, string> = {
  signature: "signature",
  initials: "initials",
  date_signed: "date_signed",
  checkbox: "checkbox",
  radio_button: "radio",
  drop_down: "dropdown",
  company_name: "company",
  name: "name",
  email: "email",
  title: "title",
  text: "text",
  date: "date",
  attachment: "attachment",
};

/** The name core resolves a document by: the stored unsigned PDF. */
export const reviewFileName = (file: RequestFile): string =>
  file.unsignedPdf || file.unsignedDoc || "";

/**
 * What a dismissal is remembered by: the document (its stored key, unique per upload) and the finding. A
 * finding id is only unique within its document -- core names a "not reviewed" notice by a hash of the file
 * name, and two names can share one -- so the id alone could dismiss another document's finding.
 */
export const dismissalKey = (fileName: string, findingId: string): string =>
  JSON.stringify([fileName, findingId]);

/** A document's name as the editor shows it: the stored name without any folder in front. */
export const documentDisplayName = (fileName: string): string =>
  fileName.split("/").pop() || fileName;

/**
 * Plain text the AI accepts: it refuses HTML tags and Markdown links outright, and caps every string at
 * 200 characters.
 */
const cleanText = (value?: string | null): string | undefined => {
  const text = (value ?? "")
    .replace(/[<>]/g, "")
    .replace(/\]\(/g, "] (")
    .replace(/\s+/g, " ")
    .trim();
  return text ? text.slice(0, MAX_REVIEW_TEXT) : undefined;
};

const signerRecipientId = (signerId: number) => `signer-${signerId}`;

const clampPercent = (value: number) =>
  Math.round(Math.min(Math.max(value, 0), 100) * 100) / 100;

/**
 * The field's box in percent of its page. `x/y/w/h` are PDF points once `setCoordinates` has run; the
 * editor's own percentages are the fallback. `undefined` lets the AI locate the field by its label.
 */
const rectOf = (field: FileField, pageSize?: { x: number; y: number }) => {
  let box: [number, number, number, number] | null = null;
  if (pageSize?.x && pageSize?.y && field.w > 0 && field.h > 0) {
    box = [
      (field.x / pageSize.x) * 100,
      (field.y / pageSize.y) * 100,
      ((field.x + field.w) / pageSize.x) * 100,
      ((field.y + field.h) / pageSize.y) * 100,
    ];
  } else if (field.fieldPageWidth && field.fieldPageHeight) {
    const x = field.fieldPageX ?? 0;
    const y = field.fieldPageY ?? 0;
    box = [x, y, x + field.fieldPageWidth, y + field.fieldPageHeight];
  }
  if (!box) return undefined;
  const [x0, y0, x1, y1] = box.map(clampPercent);
  return x0 < x1 && y0 < y1 ? { x0, y0, x1, y1 } : undefined;
};

const recipientRole = (participant: RequestParticipant) => {
  if (participant.cc) return "cc";
  return participant.deliveryMode === strings.deliveryModes.inPerson
    ? "signer_in_person"
    : "signer";
};

/**
 * The review's request, built from the request exactly as it is about to be sent (after
 * `setCoordinates`), so the boxes reviewed are the boxes that go out. `null` when the envelope is past
 * what the AI reviews (see `MAX_REVIEW_FIELDS`): the caller then goes on without a review.
 */
export const buildPresendReviewPayload = (
  request: SignRequest,
): PresendReviewRequest | null => {
  const signers = request.signers ?? [];
  const reviewers = request.approval?.approvalNeeded
    ? (request.approval.reviewers ?? [])
    : [];
  const recipients: PresendReviewRecipient[] = [
    ...signers.map((signer) => ({
      id: signerRecipientId(signer.id),
      name: cleanText(signer.name),
      email: cleanText(signer.email),
      role: recipientRole(signer),
    })),
    ...reviewers.map((reviewer) => ({
      id: `approver-${reviewer.id}`,
      name: cleanText(reviewer.name),
      email: cleanText(reviewer.email),
      role: "approver",
    })),
  ];

  // An any-signer field is filled by whichever signer gets to it, so it is owed by a signer -- the
  // first one stands in, which keeps it from reading as "assigned to nobody".
  const firstSigner = signers.find((signer) => !signer.cc);
  const anySignerId = firstSigner ? signerRecipientId(firstSigner.id) : undefined;

  const fields: PresendReviewField[] = [];
  for (const file of request.files ?? []) {
    const fileName = reviewFileName(file);
    if (!fileName) continue;
    for (const field of file.fields ?? []) {
      if (field.hidden || typeof field.page !== "number" || field.page < 0) {
        continue;
      }
      const type = REVIEW_FIELD_TYPES[(field.type ?? "").replace(/\s+/g, "_").toLowerCase()];
      const prefilled = Boolean(field.value || field.defaultValue);
      fields.push({
        id: String(field.id),
        fileName,
        page: field.page,
        rect: rectOf(field, file.dimensions?.[field.page]),
        label: cleanText(field.label || field.tabLabel),
        type,
        required: Boolean(field.required),
        recipientId:
          field.signerIndex === ANY_SIGNER
            ? anySignerId
            : signerRecipientId(field.signerIndex),
        ...(prefilled ? { filled: true } : {}),
      });
    }
  }

  if (
    recipients.length > MAX_REVIEW_RECIPIENTS ||
    fields.length > MAX_REVIEW_FIELDS
  ) {
    return null;
  }
  return {
    files: (request.files ?? []).map(reviewFileName).filter(Boolean),
    recipients,
    fields,
  };
};

const count = (value: unknown) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : 0;
};

const text = (value: unknown): string | null =>
  typeof value === "string" && value.trim() ? value : null;

/**
 * An id for a finding the server sent without one, made of what the finding says rather than where it sits
 * in the list: the same finding gets the same id on the next review, so a dismissal sticks to it, and a
 * different finding that lands in its place never inherits the dismissal.
 */
const fallbackFindingId = (finding: any): string =>
  `auto:${JSON.stringify([
    text(finding.kind),
    text(finding.category),
    text(finding.title),
    count(finding.page),
    text(finding.fieldId),
  ])}`;

/**
 * The review as the popup can render it, or `null` when the answer is not a review at all. Every list
 * and string the popup reads is checked here, so an unexpected shape degrades to "Review unavailable"
 * or a shorter list -- never to a render error that takes the editor down with it.
 */
export const normalisePresendReview = (
  value: any,
): PresendReviewResponse | null => {
  if (!value || typeof value !== "object" || !Array.isArray(value.documents)) {
    return null;
  }
  const documents = value.documents
    .filter((document: any) => document && typeof document === "object")
    .map((document: any) => ({
      documentId: text(document.documentId),
      fileName: text(document.fileName) ?? "",
      status: text(document.status) ?? "not_reviewed",
      findings: (Array.isArray(document.findings) ? document.findings : [])
        .filter(
          (finding: any) =>
            finding && typeof finding === "object" && text(finding.title),
        )
        .map((finding: any) => ({
          id: text(finding.id) ?? fallbackFindingId(finding),
          category: text(finding.category) ?? "",
          severity: text(finding.severity) ?? "low",
          title: finding.title,
          detail: text(finding.detail),
          suggestion: text(finding.suggestion),
          page: count(finding.page) || null,
          snippet: text(finding.snippet),
          fieldId: text(finding.fieldId),
          recipientId: text(finding.recipientId),
          kind: text(finding.kind) ?? "",
        })),
    }));
  return {
    status: text(value.status) ?? "not_reviewed",
    summary: {
      title: text(value.summary?.title) ?? "",
      message: text(value.summary?.message) ?? "",
    },
    counts: {
      high: count(value.counts?.high),
      medium: count(value.counts?.medium),
      low: count(value.counts?.low),
      notChecked: count(value.counts?.notChecked),
    },
    documents,
  };
};

// --------------------------------------------------------------------------------------------- display

export const isIssue = (finding: PresendFinding) => finding.kind === "issue";
export const isNotChecked = (finding: PresendFinding) =>
  finding.kind === "not_checked";
export const isInfo = (finding: PresendFinding) => finding.kind === "info";

export interface FindingGroup {
  category: string;
  heading: string;
  findings: PresendFinding[];
}

/**
 * A document's issues grouped by category. The AI sorts findings most severe first, so a group's place
 * is where its first finding sits -- the most severe group leads.
 */
export const groupIssues = (findings: PresendFinding[]): FindingGroup[] => {
  const groups: FindingGroup[] = [];
  for (const finding of findings.filter(isIssue)) {
    const known = finding.category in copy.categories;
    const category = known ? finding.category : "other";
    let group = groups.find((g) => g.category === category);
    if (!group) {
      group = {
        category,
        heading: copy.categories[category as keyof typeof copy.categories],
        findings: [],
      };
      groups.push(group);
    }
    group.findings.push(finding);
  }
  return groups;
};

/** "2 issues", "Checked · no issues", "No issues · 1 couldn't check". */
export const documentMeta = (document: PresendReviewDocument): string => {
  const issues = document.findings.filter(isIssue).length;
  const notChecked = document.findings.filter(isNotChecked).length;
  const head = issues
    ? issues === 1
      ? copy.issueOne
      : fillCopy(copy.issues, { count: issues })
    : document.status === "ready"
      ? copy.checkedNoIssues
      : copy.noIssues;
  return notChecked
    ? `${head} · ${fillCopy(copy.couldNotCheck, { count: notChecked })}`
    : head;
};

/** The header's count chips, in the design's order; zero counts are left out. */
export const countChips = (counts: PresendReviewResponse["counts"]) =>
  [
    { key: "high", label: copy.countHigh, count: counts?.high ?? 0 },
    { key: "medium", label: copy.countMedium, count: counts?.medium ?? 0 },
    { key: "low", label: copy.countLow, count: counts?.low ?? 0 },
    {
      key: "notChecked",
      label: copy.countNotChecked,
      count: counts?.notChecked ?? 0,
    },
  ]
    .filter((chip) => chip.count > 0)
    .map((chip) => ({ ...chip, label: fillCopy(chip.label, { count: chip.count }) }));

/** The label a status pill shows; an unknown status reads as not reviewed (never as a green tick). */
export const statusLabel = (status: string) =>
  copy.status[status as keyof typeof copy.status] ?? copy.status.not_reviewed;

export const severityLabel = (severity: string) =>
  copy.severity[severity as keyof typeof copy.severity] ?? copy.severity.low;
